import { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PerDiemService } from '../service/perdiemService.mjs';
import { secureLogger } from '../../utils/secureLogger.mjs';
import { startOfDay, formatLocalDate, parseLocalDate } from '../../utils/dateOnly.mjs';
import { AuthContext } from '../../authProvider.mjs';
import { useDialog } from '../../components/dialog/DialogProvider.mjs';


/**
 * @param {boolean} adminMode
 * @param {object}  [options]
 * @param {object}  [options.onBehalfOf]  the claimant, when an admin is filing
 *   this claim for them. The form behaves exactly as it does for the claimant -
 *   same city search, same rates, same meals and lodging - and only the final
 *   submit differs: it goes to the on-behalf endpoint with their id, so the
 *   claim is theirs and they are told.
 */
// -- The 75% first/last travel day ---------------------------------------------
// A day with its "75%" box ticked carries `mieAt75Percent`, which the server
// prices from (utils/perDiemLines.mjs): 75% of each published meal and of
// incidentals, each to the cent. While a day is at 75%, `originalRates` holds
// the 75% figures - so restoring a dropped meal gives back 75%, not the full
// rate - and `fullRates` keeps the published ones for unticking.
const MIE_KEYS = ['breakfast', 'lunch', 'dinner', 'incidentals'];
const TRAVEL_DAY_SHARE = 0.75;
const to75 = (v) => Math.round((Number(v) || 0) * TRAVEL_DAY_SHARE * 100) / 100;
// Only for a day loaded at 75% with no published figures in hand; the server
// re-prices from the published rate on save, so a stray cent does not stick.
const from75 = (v) => Math.round(((Number(v) || 0) / TRAVEL_DAY_SHARE) * 100) / 100;
const sumMIE = (row) => MIE_KEYS.reduce((t, k) => t + (Number(row[k]) || 0), 0);

/**
 * The meal and incidentals figures a row shows for a set of published rates,
 * honouring its 75% box. Used wherever a row's rates are (re)filled.
 */
const ratesForRow = (row, published, lodgingCost) => {
  const at75 = row?.mieAt75Percent === true;
  const shown = {};
  for (const k of MIE_KEYS) shown[k] = at75 ? to75(published[k]) : (Number(published[k]) || 0);
  return {
    ...shown,
    totalMIE: sumMIE(shown),
    originalRates: { ...shown, lodgingCost },
    fullRates: at75 ? Object.fromEntries(MIE_KEYS.map(k => [k, Number(published[k]) || 0])) : undefined,
  };
};

export const usePerDiemForm = (adminMode = false, { onBehalfOf = null } = {}) => {
  // Custom in-app dialogs replacing window.confirm/window.alert. `alert` is
  // named to shadow the global deliberately: every existing alert(...) call in
  // this file then routes through the styled modal with no other change.
  const { confirm, alert } = useDialog();
  const { isAuditor, user } = useContext(AuthContext);

  const { claimId } = useParams();
  const inputRefs = useRef({});
  const isEditMode = !!claimId;
  const isViewMode = !!claimId;
  const [isEditing, setIsEditing] = useState(false);
  // const isReadOnly = isViewMode && !isEditing;

  const [_isDeleting, setIsDeleting] = useState(false);
  const [_deleteError, setDeleteError] = useState(null);
  const [_deleteSuccess, setDeleteSuccess] = useState(false);

  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');
  const [adminDecisionComment, setAdminDecisionComment] = useState('');

  

  // Supporting documents state
  const [supportingDocuments, setSupportingDocuments] = useState([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
  

  const navigate = useNavigate();

  // Form state
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [position, setPosition] = useState('');
  const [businessPurpose, setBusinessPurpose] = useState('');
  const [receivedAdvance, setReceivedAdvance] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [removedLodgings, setRemovedLodgings] = useState({});
  
  // Line items state
  const [lineItems, setLineItems] = useState([]);
  // Which days have their 75% box ticked, from the rows themselves - so a
  // saved claim reopens with its boxes ticked (a separate map was lost on
  // every reload, and nothing it held ever reached the server).
  const appliedRates = useMemo(
    () => Object.fromEntries(lineItems.filter(i => i.mieAt75Percent === true).map(i => [i.id, true])),
    [lineItems]
  );

  const [_openComments, _setOpenComments] = useState({});

  
  // City search state
  const [citySearch, setCitySearch] = useState('');
  const [activeSearchId, setActiveSearchId] = useState(null);
  const [cityResults, setCityResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchRef = useRef(null);

  const [showCustomCityInput, setShowCustomCityInput] = useState(null); // null, 'quickFill', or itemId
  const [customCityName, setCustomCityName] = useState('');
  const [customCityCountry, setCustomCityCountry] = useState('');


  const [quickFillCity, setQuickFillCity] = useState('');
  const [quickFillCountry, setQuickFillCountry] = useState('');

  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [claimStatus, setClaimStatus] = useState('draft');
  // Id of a draft created in this session (the create route has no URL param),
  // so Delete Draft is reachable without first going back via the dashboard.
  const [savedDraftId, setSavedDraftId] = useState(null);
  const [claimData, setClaimData] = useState(null);
  const [adminComments, setAdminComments] = useState('');
  const [processedBy, setProcessedBy] = useState(null);
  const [processedAt, setProcessedAt] = useState(null);



  const isAdminMode = adminMode || window.location.pathname.includes('/admin/');
  const [_isNewClaim, _setIsNewClaim] = useState(true);
  
  // Check if this is a create route
  const isCreateRoute = useMemo(() => {
    return !!onBehalfOf || window.location.pathname.includes('/perdiem/create');
  }, [onBehalfOf]);
 

  const canEditClaim = useMemo(() => {
    
      // Always allow editing for create routes
      if (isCreateRoute) return true;
      
      // If no claimId, it's a new claim - always allow editing
      if (!claimId) return true;
      
      // For existing claims, only allow editing if status is draft or returned
      return claimStatus === 'draft' || claimStatus === 'returned';
    }, [claimStatus, claimId, isCreateRoute]);

  const isReadOnly = useMemo(() => {
    // Auditors review OTHER PEOPLE's claims read-only. They open those through
    // the /admin/ routes, which would otherwise grant edit rights, so this keys
    // off the ROLE rather than the URL - but it must not extend to their own
    // claims. An auditor is also an employee who files per diem and expenses,
    // and an unconditional lock here left them unable to fill in a new claim at
    // all (the backend never restricted this; the form was simply read-only).
    const reviewingSomeoneElse =
      claimData?.user && user?.id && String(claimData.user.id) !== String(user.id);
    if (isAuditor && (isAdminMode || reviewingSomeoneElse)) return true;
    // A claim sitting in a report under auditor/director review is frozen even
    // for admins; it unlocks only when the report is sent back for revision.
    if (claimData?.reportLocked) return true;
    if (isAdminMode) return false; // Admins can always edit
    // Editable claims (create route, the user's own draft or returned claim) are
    // directly editable - no separate "Edit" click needed - so the Save/Submit
    // actions are available. Everything else is view-only.
    return !canEditClaim;
  }, [isAdminMode, canEditClaim, claimData, isAuditor, user]);

  // Debounce effect for search - use a ref to always get latest searchCities
  const searchCitiesRef = useRef(null);

  // Keep ref updated with latest searchCities function
  useEffect(() => {
    searchCitiesRef.current = searchCities;
  });

  useEffect(() => {
    if (debouncedSearch && debouncedSearch.length >= 2 && activeSearchId) {
      const timer = setTimeout(() => {
        // Use ref to always call latest searchCities function
        if (searchCitiesRef.current) {
          searchCitiesRef.current(debouncedSearch, activeSearchId);
        }
      }, 300); // 300ms debounce

      return () => clearTimeout(timer);
    } else if (!debouncedSearch || debouncedSearch.length < 2) {
      setCityResults([]);
    }
  }, [debouncedSearch, activeSearchId, setCityResults]);
    

  // Function to fetch supporting documents
  const fetchSupportingDocuments = useCallback(async (id) => {
    if (!id) return;
    
    try {
      setIsLoading(true);
      const response = await PerDiemService.getDocuments(id);
      
      if (response.success) {
        setSupportingDocuments(response.documents || []);
      } else {
        console.error('Failed to fetch documents:', response.message);
      }
    } catch (error) {
      console.error('Error fetching supporting documents:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);


  // Function to upload supporting documents
  const uploadSupportingDocuments = useCallback(async (filesWithDescriptions) => {
    if (!claimId || !filesWithDescriptions || filesWithDescriptions.length === 0) {
      throw new Error('Cannot upload: Missing claim ID or files');
    }
    
    setIsUploadingDocuments(true);
    
    try {
      const formData = new FormData();
      
      // Add each file with its description
      filesWithDescriptions.forEach((item) => {
        formData.append('documents', item.file);
        formData.append('descriptions[]', item.description);
      });

      const response = await PerDiemService.uploadDocuments(claimId, formData);
      
      if (response.success) {
        // Add new documents to state
        setSupportingDocuments(prev => [...prev, ...(response.documents || [])]);
        return response.documents;
      } else {
        throw new Error(response.message || 'Failed to upload documents');
      }
    } catch (error) {
      console.error('Error uploading documents:', error);
      throw error;
    } finally {
      setIsUploadingDocuments(false);
    }
  }, [claimId]);

  // Function to delete supporting documents
  const deleteSupportingDocument = useCallback(async (documentId) => {
    // Make sure we're checking the values properly
    if (!claimId || claimId.trim() === '') {
      secureLogger.error('Claim ID is missing or empty');
      throw new Error('Cannot delete: Missing claim ID');
    }

    if (!documentId || documentId.trim() === '') {
      secureLogger.error('Document ID is missing or empty');
      throw new Error('Cannot delete: Missing document ID');
    }

    // Convert to string if they're not already (in case they're ObjectIds)
    const claimIdStr = String(claimId);
    const documentIdStr = String(documentId);
    
    setIsUploadingDocuments(true);
    
    try {
      const response = await PerDiemService.deleteDocument(claimIdStr, documentIdStr);
      
      if (response.success) {
        // Remove document from state
        setSupportingDocuments(prev => 
          prev.filter(doc => {
            const docIdToCheck = String(doc.id || doc._id);
            return docIdToCheck !== documentIdStr;
          })
        );
        return true;
      } else {
        throw new Error(response.message || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    } finally {
      setIsUploadingDocuments(false);
    }
  }, [claimId]);

  // Fetch existing claim data if in edit mode
  const loadClaimData = useCallback(async (claimId) => {
    
    try {
      setIsLoading(true);
      const response = await PerDiemService.getPerDiemClaimByID(claimId);
     
      if (response.data) {
        const claim = response.data;
        
        // Set claim status and metadata
        setClaimStatus(claim.status || 'draft');
        setClaimData(claim);
        setAdminComments(claim.adminComments || '');
        setProcessedBy(claim.processedBy || null);
        setProcessedAt(claim.processedAt || null);
        
        // Populate form data
        setBusinessPurpose(claim.reasonForTrip || '');
        setStartDate(claim.startDate ? new Date(claim.startDate) : null);
        setEndDate(claim.endDate ? new Date(claim.endDate) : null);
        
        // Load line items with admin comments
        if (claim.dailyDetails && claim.dailyDetails.length > 0) {
          const formattedLineItems = claim.dailyDetails.map((detail, index) => ({
            id: detail._id || `item-${index}`,
            // parseLocalDate, NOT new Date(): the server sends a date-only
            // "2026-04-15", which new Date() reads as UTC midnight while a
            // user-picked row is LOCAL midnight. The two representations then
            // compared as different calendar days (west of UTC the loaded row
            // read as the 14th), so the duplicate-day highlight and the
            // submit-time check both missed genuine clashes. One representation
            // everywhere is what makes those checks reliable.
            date: parseLocalDate(detail.date),
            city: detail.city || '',
            country: detail.country || '',
            stateDeptMIE: detail.perDiemRate || 0,
            breakfast: detail.breakfast || 0,
            lunch: detail.lunch || 0,
            dinner: detail.dinner || 0,
            incidentals: detail.incidentals || 0,
            totalMIE: detail.totalMIE || 0,
            lodgingCost: detail.lodgingCost || 0,
            runningTotal: detail.runningTotal || 0,
            comments: detail.comments || '',
            adminComments: detail.adminComments || '', // Include admin comments
            // A day saved at 75% reopens ticked; its stored figures ARE the
            // 75% ones, so the published ones are worked back for unticking.
            mieAt75Percent: detail.mieAt75Percent === true,
            fullRates: detail.mieAt75Percent === true
              ? Object.fromEntries(MIE_KEYS.map(k => [k, from75(detail[k])]))
              : undefined,
            originalRates: {
              breakfast: detail.breakfast || 0,
              lunch: detail.lunch || 0,
              dinner: detail.dinner || 0,
              incidentals: detail.incidentals || 0,
              lodgingCost: detail.lodgingCost || 0
            }
          }));
          
          setLineItems(formattedLineItems);
        }
        setSupportingDocuments(claim.supportingDocuments)
        // Fetch supporting documents
        await fetchSupportingDocuments(claimId);
      }
    } catch (error) {
      console.error('Error loading claim data:', error);
      setError('Failed to load claim data');
    } finally {
      setIsLoading(false);
    }
  }, [setBusinessPurpose, setStartDate, setEndDate, setLineItems, setIsLoading, setError, fetchSupportingDocuments]);
    
  // Update enableEditing function
  const enableEditing = useCallback(() => {
    if (!canEditClaim) {
      setError(`Cannot edit claim with status: ${claimStatus}. Only draft and returned claims can be edited.`);
      return;
    }
    setIsEditing(true);
  }, [canEditClaim, claimStatus, setError, setIsEditing]);

  // Search cities & countries
  const searchCities = useCallback(async (query, itemId) => {
    if (query.length < 2) {
      setCityResults([]);
      return;
    }

    setIsSearching(true);
    setActiveSearchId(itemId);

    try {
      let results = [];

      if (itemId === 'quickFillCountry') {
        // Use the dedicated country search endpoint
        const countryResults = await PerDiemService.searchCountries(query);

        if (!countryResults || !Array.isArray(countryResults)) {
          setCityResults([]);
          setIsSearching(false);
          return;
        }

        // No second filter here. This used to keep only countries whose name
        // contained the typed letters - needed when the server returned CITY
        // rows that dragged in unrelated countries, but it also threw away the
        // server's alias matches: "USA" came back as United States and was then
        // discarded, because "united states" does not contain "usa". The server
        // now returns countries only, already matched, so its answer is used as
        // given.
        const filteredCountries = countryResults.filter(result => !!result.country);

        // DEDUPLICATE: Remove duplicate countries
        const seenCountries = new Set();
        const uniqueCountries = filteredCountries.filter(result => {
          const countryLower = result.country.toLowerCase();
          if (seenCountries.has(countryLower)) return false;
          seenCountries.add(countryLower);
          return true;
        });

        // Use backend country field for display
        results = uniqueCountries.map((result, index) => ({
          ...result,
          id: result.id || `country-${index}-${result.country}`,
          name: result.country,
          display: result.country,
        }));

      } else if (itemId === 'quickFillCity') {
        // For quick fill city search - filter by selected country
        let rawResults;
        if (quickFillCountry && quickFillCountry.trim()) {
          rawResults = await PerDiemService.searchCities(query, quickFillCountry);
        } else {
          rawResults = await PerDiemService.searchCities(query);
        }

        if (!Array.isArray(rawResults)) {
          rawResults = [];
        }

        // CLIENT-SIDE FILTERING: Only show results where city name OR country matches query
        const queryLower = query.toLowerCase();
        const filteredResults = rawResults.filter(r => {
          const cityMatch = r.name && r.name.toLowerCase().includes(queryLower);
          const countryMatch = r.country && r.country.toLowerCase().includes(queryLower);
          return cityMatch || countryMatch;
        });

        // DEDUPLICATE: Remove duplicate entries based on name + country combination
        const seen = new Set();
        const uniqueResults = filteredResults.filter(r => {
          const key = `${r.name}-${r.country}`.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        // Add unique IDs to prevent React key warnings
        results = uniqueResults.map((r, index) => ({
          ...r,
          id: `qf-city-${index}-${r.name}-${r.country}`
        }));

        // ADD "OTHER" OPTION FOR CITY SEARCHES
        const hasExactCityMatch = results.some(r =>
          r.name && r.name.toLowerCase() === queryLower
        );

        if (!hasExactCityMatch && results.length > 0) {
          const otherOption = {
            id: `qf-other-city-${Date.now()}`,
            name: 'Other',
            country: quickFillCountry || results[0]?.country || 'Custom',
            display: query,
            isOther: true,
            customInput: query
          };
          results.push(otherOption);
        }

      } else {
        // For regular line item city search
        const currentItem = lineItems.find(item => item.id === itemId);
        let itemCountry = currentItem?.country;

        // Extract country from city string if it exists
        if (!itemCountry && currentItem?.city) {
          const cityParts = currentItem.city.split(',');
          if (cityParts.length > 1) {
            itemCountry = cityParts[cityParts.length - 1].trim();
          }
        }

        let rawResults;
        if (itemCountry && itemCountry.trim()) {
          rawResults = await PerDiemService.searchCities(query, itemCountry);
        } else {
          rawResults = await PerDiemService.searchCities(query);
        }

        if (!Array.isArray(rawResults)) {
          rawResults = [];
        }

        // CLIENT-SIDE FILTERING: Only show results where city name OR country matches query
        const queryLower = query.toLowerCase();
        const filteredResults = rawResults.filter(r => {
          const cityMatch = r.name && r.name.toLowerCase().includes(queryLower);
          const countryMatch = r.country && r.country.toLowerCase().includes(queryLower);
          return cityMatch || countryMatch;
        });

        // DEDUPLICATE: Remove duplicate entries based on name + country combination
        const seen = new Set();
        const uniqueResults = filteredResults.filter(r => {
          const key = `${r.name}-${r.country}`.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        // Add unique IDs to prevent React key warnings
        results = uniqueResults.map((r, index) => ({
          ...r,
          id: `li-city-${index}-${r.name}-${r.country}`
        }));

        // ADD "OTHER" OPTION
        const hasExactCityMatch = results.some(r =>
          r.name && r.name.toLowerCase() === queryLower
        );

        if (!hasExactCityMatch && results.length > 0) {
          const otherOption = {
            id: `li-other-city-${Date.now()}`,
            name: 'Other',
            country: itemCountry || results[0]?.country || 'Custom',
            display: query,
            isOther: true,
            customInput: query
          };
          results.push(otherOption);
        }
      }

      setCityResults(results || []);
      setSelectedIndex(-1);

    } catch (err) {
      secureLogger.error('City search failed', err.message);
      setError('Failed to search cities. Please try again.');
      setCityResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [quickFillCountry, lineItems, setError, setCityResults, setSelectedIndex, setIsSearching, setActiveSearchId]);



// Add a new line item
const addLineItem = useCallback((initialValues = {}) => {
  setLineItems(prevItems => {
    // Default to the first day of the trip that isn't already used, instead of
    // today - a new row used to land outside the travel window (and duplicate an
    // existing day) with nothing to catch it.
    const usedDays = new Set(
      prevItems.map(i => (startOfDay(i.date) || new Date(0)).getTime())
    );
    let defaultDate = startOfDay(startDate) || startOfDay(new Date());
    const lastDay = startOfDay(endDate);
    while (lastDay && defaultDate <= lastDay && usedDays.has(defaultDate.getTime())) {
      defaultDate = new Date(defaultDate.getFullYear(), defaultDate.getMonth(), defaultDate.getDate() + 1);
    }
    // Every day of the trip is taken: there is no day to give a new line, and
    // the loop above has walked one past the end. Adding nothing is right -
    // the page tells the traveller why before it gets here.
    if (lastDay && defaultDate > lastDay && !initialValues.date) {
      return prevItems;
    }

    const newItem = {
      // crypto.randomUUID, not Date.now(): two rows added within the same
      // millisecond shared an id, which collided as React keys and made the
      // duplicate-day highlight land on the wrong row (and confused the
      // exclude-self check in isDuplicateDate). Matches Quick Fill's ids.
      id: crypto.randomUUID(),
      date: defaultDate,
      city: '',
      country: '',
      stateDeptMIE: 0,
      breakfast: 0,
      lunch: 0,
      dinner: 0,
      incidentals: 0,
      lodgingCost: 0,
      totalMIE: 0,
      runningTotal: 0, // Will be recalculated below
      comments: '',
      originalRates: {
        breakfast: 0,
        lunch: 0,
        dinner: 0,
        incidentals: 0,
        lodgingCost: 0
      },
      ...initialValues
    };

    const updatedItems = [...prevItems, newItem];

    // Recalculate runningTotal for all
    let runningTotal = 0;
    return updatedItems.map(item => {
      const mie = parseFloat(item.totalMIE) || 0;
      const lodging = parseFloat(item.lodgingCost) || 0;
      runningTotal += mie + lodging;
      return { ...item, runningTotal };
    });
  });
}, [startDate, endDate]);


// Helper function to calculate running total
const _calculateNewRunningTotal = (items) => {
  return items.reduce((total, item) => 
    total + Number(item.totalMIE || 0) + Number(item.lodgingCost || 0), 0);
};


  // Update line item
  const updateLineItem = useCallback((id, field, value) => {
    setLineItems(prevItems => {
      const updatedItems = prevItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };

          // Normalize values (if cleared, use 0)
          const breakfast = parseFloat(updatedItem.breakfast) || 0;
          const lunch = parseFloat(updatedItem.lunch) || 0;
          const dinner = parseFloat(updatedItem.dinner) || 0;
          const incidentals = parseFloat(updatedItem.incidentals) || 0;
          const lodgingCost = parseFloat(updatedItem.lodgingCost) || 0;

          updatedItem.totalMIE = breakfast + lunch + dinner + incidentals;
          updatedItem.lodgingCost = lodgingCost; // Normalize in case field was cleared

          return updatedItem;
        }
        return item;
      });

      // Recalculate running totals sequentially
      let runningTotal = 0;
      return updatedItems.map(item => {
        const mie = parseFloat(item.totalMIE) || 0;
        const lodging = parseFloat(item.lodgingCost) || 0;
        runningTotal += mie + lodging;
        return { ...item, runningTotal };
      });
    });
  }, []);


  // Handle city selection
  const handleCitySelect = useCallback(async (id, city, country) => {
    // IMPORTANT: Set citySearch to the selected value BEFORE clearing activeSearchId
  // This ensures the input shows the value regardless of React state batching order
  // Input logic: value={activeSearchId === item.id ? citySearch : item.city}
  const selectedValue = city === 'Other' ? `Other (${country})` : `${city}, ${country}`;
  setCitySearch(selectedValue);  // Shows if activeSearchId hasn't updated yet
  setCityResults([]);
  setSelectedIndex(-1);
  setActiveSearchId(null);

  // Handle "Other" selection - show custom input instead of prompt
  if (city === 'Other') {
    setCustomCityCountry(country);
    setCustomCityName('');
    setShowCustomCityInput(id); // Store which item needs custom city
    return;
  }

  // Handle quick fill city selection
  if (id === 'quickFillCity') {
    setQuickFillCity(city);
    setQuickFillCountry(country);

    // Blur the input to close any mobile keyboards
    const input = document.getElementById('quickFillCityInput');
    if (input) {
      input.blur();
    }
    return;
  }

  // Handle quick fill country selection
  if (id === 'quickFillCountry') {
    setQuickFillCountry(city); // For country, city parameter contains the country name
    setQuickFillCity(''); // Clear city when country changes

    const input = document.getElementById('quickFillCountryInput');
    if (input) {
      input.blur();
    }
    return;
  }

  // Handle regular line item city selection
  setIsLoading(true);

  try {
    // Update line item with selected city
    updateLineItem(id, 'city', selectedValue);
    updateLineItem(id, 'country', country);

    // Clear removed lodgings for this item. The 75% box is kept: it is the
    // row's own flag, and the new place's rates are filled in at 75%.
    setRemovedLodgings(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });

    // Fetch per diem rates
    const currentItem = lineItems.find(item => item.id === id);
    const itemDate = currentItem?.date || new Date();

    const rates = await PerDiemService.getPerDiemRates(itemDate, city, country);

    setLineItems(prevItems => {
      const updatedItems = prevItems.map(item => {
        if (item.id !== id) return item;

        const lodging = Number(rates.lodging) || 0;

        return {
          ...item,
          city: `${city}, ${country}`,
          country,
          stateDeptMIE: rates.mie || 0,
          // The new place's rates, at 75% if the day's box is ticked.
          ...ratesForRow(item, rates, lodging),
          lodgingCost: lodging,
          // Which seasonal rate this date resolved to (display only).
          rateSeason: rates.season || null,
          rateMonth: rates.dataMonth || null,
          // Alaska, Hawaii and the US territories are set by the Department of
          // Defense, which publishes no machine-readable file, so no rate is
          // returned for them. Carry the flag and reason so the row can say that
          // outright - otherwise the zeros read as "this location is worth $0"
          // rather than "you need to type the rate in".
          manualEntryRequired: !!rates.manualEntryRequired,
          rateNotice: rates.manualEntryRequired ? (rates.message || null) : null
        };
      });

      // Recalculate running totals
      let runningTotal = 0;
      return updatedItems.map(item => {
        const mie = parseFloat(item.totalMIE) || 0;
        const lodging = parseFloat(item.lodgingCost) || 0;
        runningTotal += mie + lodging;
        return { ...item, runningTotal };
      });
    });

  } catch (err) {
    console.error('Error getting per diem rates:', err);
    setError('Failed to get per diem rates. Please try again.');
  } finally {
    setIsLoading(false);
    
    // Blur the input element to close mobile keyboards
    const inputElement = inputRefs.current[id];
    if (inputElement) {
      inputElement.blur();
    }
  }
}, [lineItems, updateLineItem, setError, inputRefs, setQuickFillCity, setQuickFillCountry, setCityResults, setCitySearch, setSelectedIndex, setActiveSearchId, setCustomCityCountry, setCustomCityName, setShowCustomCityInput]);

    // Handle custom city submission
    const handleCustomCitySubmit = useCallback(async () => {
      if (!customCityName.trim()) {
        setError('Please enter a city name');
        return;
      }

      const trimmedCityName = customCityName.trim();
      const itemId = showCustomCityInput;

      try {
        if (itemId === 'quickFillCity') {
          // Handle quick fill custom city 
          setQuickFillCity(trimmedCityName);
          setQuickFillCountry(customCityCountry);
          
          // Close the custom city input
          setShowCustomCityInput(null);
          setCustomCityName('');
          setCustomCityCountry('');
          
          // Clear search state
          setCityResults([]);
          setActiveSearchId(null);
          setCitySearch('');
          
          return; // Exit early for quickFill
        } else {
          // Handle line item custom city (existing code remains the same)
          setIsLoading(true);
          
          updateLineItem(itemId, 'city', `${trimmedCityName}, ${customCityCountry}`);
          updateLineItem(itemId, 'country', customCityCountry);
          
          // Try to get per diem rates for the custom city
          try {
            const currentItem = lineItems.find(item => item.id === itemId);
            const itemDate = currentItem?.date || new Date();
            const rates = await PerDiemService.getPerDiemRates(itemDate, trimmedCityName, customCityCountry);
            
            const lodging = Number(rates.lodging) || 0;

            setLineItems(prevItems => {
              const updatedItems = prevItems.map(item => {
                if (item.id !== itemId) return item;

                return {
                  ...item,
                  city: `${trimmedCityName}, ${customCityCountry}`,
                  country: customCityCountry,
                  stateDeptMIE: rates.mie || 0,
                  // The new place's rates, at 75% if the day's box is ticked.
                  ...ratesForRow(item, rates, lodging),
                  lodgingCost: lodging,
                  // Which seasonal rate this date resolved to (display only).
                  rateSeason: rates.season || null,
                  rateMonth: rates.dataMonth || null
                };
              });

              // Recalculate running totals
              let runningTotal = 0;
              return updatedItems.map(item => {
                const mie = parseFloat(item.totalMIE) || 0;
                const lodging = parseFloat(item.lodgingCost) || 0;
                runningTotal += mie + lodging;
                return { ...item, runningTotal };
              });
            });
          } catch (rateError) {
            console.warn('Could not fetch rates for custom city:', rateError);
            // Set default values if rates can't be fetched
            updateLineItem(itemId, 'stateDeptMIE', 0);
            updateLineItem(itemId, 'breakfast', 0);
            updateLineItem(itemId, 'lunch', 0);
            updateLineItem(itemId, 'dinner', 0);
            updateLineItem(itemId, 'incidentals', 0);
            updateLineItem(itemId, 'lodgingCost', 0);
          }
        }

        // Close the custom city input (for line items)
        setShowCustomCityInput(null);
        setCustomCityName('');
        setCustomCityCountry('');
        
      } catch (error) {
        console.error('Error submitting custom city:', error);
        setError('Failed to set custom city. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }, [customCityName, customCityCountry, showCustomCityInput, lineItems, updateLineItem, setError, setQuickFillCity, setQuickFillCountry, setCityResults, setActiveSearchId, setCitySearch]);

  // Handle custom city cancellation
  const handleCustomCityCancel = useCallback(() => {
    setShowCustomCityInput(null);
    setCustomCityName('');
    setCustomCityCountry('');
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e, itemId) => {
    // Fix: Add itemId parameter to get the current item being processed
    if (cityResults.length === 0) return;
    
    // Down arrow
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev < cityResults.length - 1 ? prev + 1 : prev));
    }
    // Up arrow
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
    }
    // Enter
    else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      const city = cityResults[selectedIndex];
      handleCitySelect(itemId, city.name, city.country);
    }
    // Escape
    else if (e.key === 'Escape') {
      e.preventDefault();
      setCityResults([]);
    }
  }, [cityResults, selectedIndex, handleCitySelect]);





/**
 * Tick or untick a day's 75% box.
 *
 * The box is the row's `mieAt75Percent` flag, and it is the flag - not the
 * figures - that is saved: the server prices the day from it. The figures here
 * only have to show what the server will store. A meal already given up (0)
 * stays given up either way.
 */
const apply75Percent = useCallback((id) => {
  if (!id) return;
  setLineItems(prevItems => {
    const updatedItems = prevItems.map(item => {
      if (item.id !== id) return item;
      const turningOn = item.mieAt75Percent !== true;
      // The published figures: kept aside while at 75%, otherwise the row's
      // reference rates are the published ones already.
      const published = item.fullRates
        || (item.mieAt75Percent === true
          ? Object.fromEntries(MIE_KEYS.map(k => [k, from75(item.originalRates?.[k] ?? item[k])]))
          : Object.fromEntries(MIE_KEYS.map(k => [k, Number(item.originalRates?.[k] ?? item[k]) || 0])));

      const next = { ...item, mieAt75Percent: turningOn };
      const reference = { ...(item.originalRates || {}) };
      for (const k of MIE_KEYS) {
        const rate = turningOn ? to75(published[k]) : (Number(published[k]) || 0);
        reference[k] = rate;
        next[k] = (Number(item[k]) || 0) > 0 ? rate : 0;
      }
      next.originalRates = reference;
      next.fullRates = turningOn ? published : undefined;
      next.totalMIE = sumMIE(next);
      return next;
    });

    // Recalculate running totals
    let runningTotal = 0;
    return updatedItems.map(item => {
      runningTotal += Number(item.totalMIE || 0) + Number(item.lodgingCost || 0);
      return { ...item, runningTotal };
    });
  });
}, []);



// Modified removeLodging function with safety checks
const removeLodging = useCallback((id) => {
  if (!id) return; // Safety check
  
  // Check if already removed to toggle back
  const isLodgingRemoved = removedLodgings[id];
  
  if (isLodgingRemoved) {
    // Toggle off - restore original lodging
    setLineItems(prevItems => {
      const updatedItems = prevItems.map(item => {
        if (item.id === id) {
          return { ...item, lodgingCost: isLodgingRemoved || 0 };
        }
        return item;
      });
      
      // Recalculate running totals
      let runningTotal = 0;
      return updatedItems.map(item => {
        runningTotal += Number(item.totalMIE || 0) + Number(item.lodgingCost || 0);
        return { ...item, runningTotal };
      });
    });
    
    // Remove from tracking
    setRemovedLodgings(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  } else {
    // Toggle on - remove lodging
    setLineItems(prevItems => {
      // Store original value for potential restore
      const itemToModify = prevItems.find(item => item.id === id);
      if (!itemToModify) return prevItems; // Safety check
      
      const originalLodgingCost = itemToModify.lodgingCost || 0;
      
      // Set the original lodging cost in our tracking state
      setRemovedLodgings(prev => ({
        ...prev,
        [id]: originalLodgingCost
      }));
      
      const updatedItems = prevItems.map(item => {
        if (item.id === id) {
          return { ...item, lodgingCost: 0 };
        }
        return item;
      });
      
      // Recalculate running totals
      let runningTotal = 0;
      return updatedItems.map(item => {
        runningTotal += Number(item.totalMIE || 0) + Number(item.lodgingCost || 0);
        return { ...item, runningTotal };
      });
    });
  }
}, [removedLodgings]);



// Add cleanup when deleting a line item
// Bulk variants of delete/restore. Doing these as one state update (rather than
// looping the single-item handlers) keeps the running totals correct - repeated
// setState calls would each recompute from a stale list.
const deleteLineItems = useCallback((ids) => {
  const idSet = new Set(ids);
  setActiveSearchId(prev => (idSet.has(prev) ? null : prev));

  setLineItems(prevItems => {
    const remaining = prevItems.filter(item => !idSet.has(item.id));
    let runningTotal = 0;
    return remaining.map(item => {
      runningTotal += (Number(item.totalMIE) || 0) + (Number(item.lodgingCost) || 0);
      return { ...item, runningTotal };
    });
  });
}, []);

const restoreLineItems = useCallback((ids) => {
  const idSet = new Set(ids);
  setLineItems(prevItems => {
    let runningTotal = 0;
    return prevItems.map(item => {
      let next = item;
      if (idSet.has(item.id) && item.originalRates) {
        const o = item.originalRates;
        const breakfast = Number(o.breakfast) || 0;
        const lunch = Number(o.lunch) || 0;
        const dinner = Number(o.dinner) || 0;
        const incidentals = Number(o.incidentals) || 0;
        next = {
          ...item,
          breakfast, lunch, dinner, incidentals,
          totalMIE: breakfast + lunch + dinner + incidentals,
          lodgingCost: Number(o.lodgingCost) || 0,
        };
      }
      runningTotal += (Number(next.totalMIE) || 0) + (Number(next.lodgingCost) || 0);
      return { ...next, runningTotal };
    });
  });
}, []);

const deleteLineItem = useCallback((id) => {
  // Clear search state if the deleted item was the active search
  if (activeSearchId === id) {
    setActiveSearchId(null);
    setCitySearch('');
  }
  
  setLineItems(prevItems => {
    const filteredItems = prevItems.filter(item => item.id !== id);
    
    // Recalculate running totals
    let runningTotal = 0;
    return filteredItems.map(item => {
      runningTotal += Number(item.totalMIE) + Number(item.lodgingCost);
      return { ...item, runningTotal };
    });
  });
  
  // Clean up tracking states (the 75% flag went with the row)
  setRemovedLodgings(prev => {
    const updated = { ...prev };
    delete updated[id];
    return updated;
  });
}, [activeSearchId]);  //  activeSearchId to the dependency array

  // A browser Back into this form can be served from the back/forward cache,
  // which restores the React state captured BEFORE an approval - so a claim that
  // is now approved still renders as editable. Only `persisted` restores are
  // re-read, so ordinary tab switching never discards unsaved edits.
  useEffect(() => {
    const targetId = claimId;
    if (!targetId) return;

    const handlePageShow = (event) => {
      if (event.persisted) loadClaimData(targetId);
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [claimId, loadClaimData]);

  // Line-level checks shared by the traveller's submit and the admin's save.
  // Duplicate days were only ever blocked on the traveller path, so an admin
  // could save - and then approve - a claim holding two lines for one day.
  // Returns a message describing the first problem found, or null when clean.
  const findLineItemProblems = useCallback(() => {
    const tripStart = startOfDay(startDate);
    const tripEnd = startOfDay(endDate);
    const outOfRange = [];
    const duplicates = [];
    const seenDays = new Map();

    (lineItems || []).forEach((item) => {
      const day = startOfDay(item.date);
      if (!day) return;
      const label = formatLocalDate(day);

      if (tripStart && tripEnd && (day < tripStart || day > tripEnd)) {
        if (!outOfRange.includes(label)) outOfRange.push(label);
      }
      const key = day.getTime();
      if (seenDays.has(key)) {
        if (!duplicates.includes(label)) duplicates.push(label);
      } else {
        seenDays.set(key, true);
      }
    });

    if (outOfRange.length > 0) {
      return (
        `These per diem dates fall outside the travel dates ` +
        `(${formatLocalDate(tripStart)} to ${formatLocalDate(tripEnd)}): ${outOfRange.join(', ')}. ` +
        `Correct them or adjust the travel dates.`
      );
    }

    if (duplicates.length > 0) {
      return (
        `There is more than one per diem line for the same day: ${duplicates.join(', ')}. ` +
        `Each day should appear once - remove or merge the duplicates.`
      );
    }

    return null;
  }, [lineItems, startDate, endDate]);

  // Save form (draft or final)
  const saveForm = useCallback(async (isDraft = false, isAdminEdit = false) => {
    try {
      // Start with validation - but skip validation for drafts
      if (!isDraft) {
        // Name the exact fields rather than a blanket message.
        const missingHeader = [
          !businessPurpose && 'Business Purpose',
          !startDate && 'Travel Start Date',
          !endDate && 'Travel End Date',
        ].filter(Boolean);

        if (missingHeader.length > 0) {
          setError(`Missing required information - ${missingHeader.join(', ')}`);
          return;
        }
        
        if (lineItems.length === 0) {
          setError("Please add at least one expense line");
          return;
        }

        const lineProblem = findLineItemProblems();
        if (lineProblem) {
          setError(lineProblem);
          return;
        }
      }
      
      setIsLoading(true);
      setError(null);
    
      // Format data for backend
      const formData = {
        businessPurpose,
        startDate,
        endDate,
        lineItems,
        lastName,
        firstName,
        position,
        receivedAdvance,
        // Explicitly set the status based on isDraft
        status: isDraft ? 'draft' : 'submitted',
        isDraft, // Include this flag for the backend
        // Add admin-specific fields
        isAdminEdit,
        adminComments: isAdminEdit ? lineItems.map(item => ({
          itemId: item.id,
          comment: item.adminComments || '',
          dateReference: formatLocalDate(item.date)
        })).filter(comment => comment.comment) : undefined
      };
      
      
      const formattedData = PerDiemService.formatClaimData(formData);
      
      // Submit to API - using update endpoint if editing or admin editing
      if (isEditMode || isAdminEdit) {
        // Use admin update endpoint if this is an admin edit
        if (isAdminEdit) {
          await PerDiemService.adminUpdatePerDiemClaim(claimId, formattedData, isDraft);
          alert('Admin changes saved successfully!');
          window.location.href = `/admin/dashboard`;
        } else {
          await PerDiemService.updatePerDiemClaim(claimId, formattedData, isDraft);
          alert(isDraft ? 'Draft updated successfully!' : 'Claim updated successfully!');
          window.location.href = '/dashboard';
        }
      } else {
        // Filing for someone: the same payload, sent where the owner is the
        // person named rather than whoever is signed in. Always a submit - a
        // draft in someone else's name that only the admin can reach would be
        // a claim nobody owns.
        if (onBehalfOf) {
          const res = await PerDiemService.submitOnBehalf(onBehalfOf.id, formattedData);
          alert(res?.message || `Claim filed for ${onBehalfOf.firstName} and submitted.`);
          window.location.href = '/admin/dashboard';
          return;
        }

        const created = await PerDiemService.submitPerDiemClaim(formattedData, isDraft);
        const newId = created?.data?.id || created?.data?._id || created?.data?.claim?.id;

        // A saved DRAFT stays on this page - no redirect - so the user can keep
        // working and Delete Draft is immediately available. Previously we
        // bounced to the dashboard, which is why a freshly-created draft had no
        // way to be deleted. Submitted claims still leave for the dashboard.
        if (isDraft) {
          if (newId) setSavedDraftId(newId);
          setClaimStatus('draft');
          alert('Draft saved successfully!');
        } else {
          alert('Claim submitted successfully!');
          window.location.href = '/dashboard';
        }
      }
      
    } catch (err) {
      setError(err.message || 'An error occurred while saving the form');
      console.error('Form submission error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [businessPurpose, startDate, endDate, lineItems, lastName, firstName, position, receivedAdvance, isEditMode, claimId, findLineItemProblems, alert, onBehalfOf]);



  // Calculate total amount
  const totalAmount = useMemo(() => {
  return lineItems.reduce((acc, item) => {
    const mie = Number(item.totalMIE || 0);
    const lodging = Number(item.lodgingCost || 0);
    return acc + mie + lodging;
  }, 0);
}, [lineItems]);


  // Effect to clean up city results when search is cleared
  // Only clear results for line item city searches, not quick fill searches
  useEffect(() => {
    if (citySearch.length < 2 && activeSearchId && activeSearchId !== 'quickFillCountry' && activeSearchId !== 'quickFillCity') {
      setCityResults([]);
    }
  }, [citySearch, activeSearchId]);

  const handleNoLodgingAllToggle = useCallback((checked) => {
  setLineItems(prevItems => {
    let runningTotal = 0;
    
    const updatedItems = prevItems.map(item => {
      const newLodgingCost = checked ? 0 : (item.originalRates?.lodgingCost ?? 0);
      
      const updatedItem = {
        ...item,
        lodgingCost: newLodgingCost
      };

      // Calculate running total
      const mie = Number(updatedItem.totalMIE) || 0;
      const lodging = Number(updatedItem.lodgingCost) || 0;
      runningTotal += mie + lodging;
      
      return { ...updatedItem, runningTotal };
    });

    // Update removedLodgings state to track which items had lodging removed
    setRemovedLodgings(prev => {
      const updated = { ...prev };
      
      if (checked) {
        // Store original lodging costs for all items when checking "No Lodging"
        updatedItems.forEach(item => {
          const originalCost = item.originalRates?.lodgingCost;
          if (originalCost > 0) {
            updated[item.id] = originalCost;
          }
        });
      } else {
        // Clear all removed lodgings when unchecking
        updatedItems.forEach(item => {
          delete updated[item.id];
        });
      }
      
      return updated;
    });

    return updatedItems;
  });
}, []);

  /**
   * Exclude (or restore) one meal across every line and re-total M&IE.
   *
   * `originalRates` always holds the untouched State Department figures, so
   * unticking restores the real rate rather than a zero we wrote earlier. Quick
   * Fill honours the same exclusions when generating rows, so these can be set
   * before filling instead of having to be re-applied afterwards.
   *
   * @param {'breakfast'|'lunch'|'dinner'} meal
   */
  const handleMealExclusionToggle = useCallback((meal, checked) => {
    setLineItems(prevItems => {
      let runningTotal = 0;
      return prevItems.map(item => {
        const original = item.originalRates || {};
        const value = checked ? 0 : (original[meal] ?? item[meal] ?? 0);
        const next = { ...item, [meal]: value };

        next.totalMIE =
          Number(next.breakfast || 0) +
          Number(next.lunch || 0) +
          Number(next.dinner || 0) +
          Number(next.incidentals || 0);

        runningTotal += next.totalMIE + (Number(next.lodgingCost) || 0);
        return { ...next, runningTotal };
      });
    });
  }, []);




  const toggleMealRates = (id) => {
    const isRemoved = removedLodgings[id];

    setRemovedLodgings(prev => {
      const updated = { ...prev };
      if (isRemoved) {
        delete updated[id];
      } else {
        updated[id] = lineItems.find(item => item.id === id)?.lodgingCost || 0;
      }
      return updated;
    });

    setLineItems(prevItems => {
      let runningTotal = 0;
      return prevItems.map(item => {
        if (item.id === id) {
          const updatedItem = {
            ...item,
            lodgingCost: isRemoved ? (removedLodgings[id] || item.originalRates?.lodgingCost || 0) : 0
          };
          runningTotal += Number(updatedItem.totalMIE) + Number(updatedItem.lodgingCost);
          return { ...updatedItem, runningTotal };
        }
        runningTotal += Number(item.totalMIE) + Number(item.lodgingCost);
        return { ...item, runningTotal };
      });
    });
  };


  

  const deletePerDiemClaim = async (explicitId = null) => {
    const targetId = explicitId || claimId || savedDraftId;

    if (!targetId) {
      setDeleteError('Claim ID is required');
      return { success: false, message: 'Claim ID is required' };
    }

    setIsDeleting(true);
    setDeleteError(null);
    setDeleteSuccess(false);

    try {
      const response = await PerDiemService.deletePerDiemClaimService(targetId);
      
      if (response && response.status >= 200 && response.status < 300) {
        setDeleteSuccess(true);
        navigate('/dashboard')
        return { success: true, data: response.data };
      } else {
        setDeleteError('Failed to delete claim');
        return { success: false, message: 'Failed to delete claim' };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while deleting the claim';
      setDeleteError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setIsDeleting(false);
    }
  };

  const resetDeleteState = () => {
    setDeleteError(null);
    setDeleteSuccess(false);
    setIsDeleting(false);
  };

  // Function to toggle admin comment section
const toggleAdminComment = useCallback((itemId) => {
  setLineItems(prevItems => 
    prevItems.map(item => 
      item.id === itemId 
        ? { ...item, showAdminComment: !item.showAdminComment }
        : { ...item, showAdminComment: false } // Close other comment sections
    )
  );
}, []);

// Function to save admin comment
const saveAdminComment = useCallback((itemId) => {
  // Just close the comment section - the comment is already saved in the item
  setLineItems(prevItems =>
    prevItems.map(item =>
      item.id === itemId
        ? { ...item, showAdminComment: false }
        : item
    )
  );
}, []);

// Function to restore original rates for a line item
const restoreLineItem = useCallback((itemId) => {
  setLineItems(prevItems => {
    const updatedItems = prevItems.map(item => {
      if (item.id === itemId && item.originalRates) {
        const breakfast = item.originalRates.breakfast || 0;
        const lunch = item.originalRates.lunch || 0;
        const dinner = item.originalRates.dinner || 0;
        const incidentals = item.originalRates.incidentals || 0;
        const totalMIE = breakfast + lunch + dinner + incidentals;
        const lodgingCost = item.originalRates.lodgingCost || 0;

        return {
          ...item,
          breakfast,
          lunch,
          dinner,
          incidentals,
          totalMIE,
          lodgingCost
        };
      }
      return item;
    });

    // Recalculate running totals
    let runningTotal = 0;
    return updatedItems.map(item => {
      runningTotal += Number(item.totalMIE || 0) + Number(item.lodgingCost || 0);
      return { ...item, runningTotal };
    });
  });
  // The 75% box stays as it was: while it is ticked, originalRates hold the
  // 75% figures, so a restored row comes back at 75%.
}, []);

const saveAdminChanges = useCallback(async () => {
  if (!claimId) {
    setError('Cannot save: missing claim ID');
    return;
  }

  // Same line-level rules as the traveller's submit - an admin edit must not be
  // able to introduce a duplicate day or a date outside the trip.
  const lineProblem = findLineItemProblems();
  if (lineProblem) {
    setError(lineProblem);
    throw new Error(lineProblem);
  }

  setIsSaving(true);
  setError(null);

  try {
    const formDataToSave = {
      businessPurpose,
      startDate,
      endDate,
      lineItems,
      lastName,
      firstName,
      position,
      receivedAdvance,
      isAdminEdit: true,
      adminComments: lineItems.map(item => ({
        itemId: item.id,
        comment: item.adminComments || '',
        dateReference: formatLocalDate(item.date)
      })).filter(comment => comment.comment)
    };

    const formattedData = PerDiemService.formatClaimData(formDataToSave);
    const response = await PerDiemService.adminUpdatePerDiemClaim(claimId, formattedData, false);

    // A day added here has an id made up in the browser; take the one the
    // server stored it under. Otherwise the PTAE panel showed the saved day as
    // removed, and the day kept its PTAE on the next save only while its date
    // stayed put (the server's fallback match).
    const savedIds = Array.isArray(response?.data?.lineIds) ? response.data.lineIds : [];
    const renamed = new Map();
    lineItems.forEach((item, i) => {
      const stored = savedIds[i];
      if (stored && String(stored) !== String(item.id)) renamed.set(String(item.id), String(stored));
    });
    if (renamed.size > 0) {
      setLineItems(prev => prev.map(item => (
        renamed.has(String(item.id)) ? { ...item, id: renamed.get(String(item.id)) } : item
      )));
    }

    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
    return true;
  } catch (err) {
    secureLogger.error('Error saving admin changes', { message: err.message });
    setError('Failed to save changes: ' + (err.response?.data?.message || err.message));
    throw err;
  } finally {
    setIsSaving(false);
  }
}, [claimId, businessPurpose, startDate, endDate, lineItems, lastName, firstName, position, receivedAdvance, findLineItemProblems]);

const handleModalSubmit = useCallback(async () => {
  if (!selectedAction) {
    setError('Please select an action');
    return;
  }

  if ((selectedAction === 'reject' || selectedAction === 'sendBack') && !adminDecisionComment.trim()) {
    setError('Comments are required for rejection or send back actions');
    return;
  }

  // A per diem claim cannot be approved without its supporting documents
  // (the user, 2026-10-21) - this used to ask "continue without documents?"
  // and let the admin through. The server refuses the same way; saying so here
  // keeps the admin on the page where the documents are uploaded. A file that
  // failed the virus scan does not count.
  if (selectedAction === 'approve') {
    const usableDocuments = (Array.isArray(supportingDocuments) ? supportingDocuments : [])
      .filter(doc => doc?.scanStatus !== 'infected');
    if (usableDocuments.length === 0) {
      setError('Attach the supporting documents for this per diem claim before approving it.');
      return;
    }
  }

  try {
    setIsLoading(true);
    setError(null);

    const claimId = new URLSearchParams(window.location.search).get('claimId') || 
                    window.location.pathname.split('/').pop();

    const statusMap = {
      'approve': 'approved',
      'reject': 'rejected', 
      'sendBack': 'returned'
    };

    // Collect any updated data from your form/UI

    const actionData = {
      action: statusMap[selectedAction],
      comments: adminDecisionComment,
      lineItemComments: lineItems
        .map((item, index) => ({
          itemId: item.id,
          // Date-only: this is the key the server matches the comment to a line
          // on, and a UTC-shifted Date matched nothing - the comment was
          // silently dropped.
          date: formatLocalDate(item.date),
          adminComments: item.adminComments || '',
          index
        }))
        .filter(comment => comment.adminComments.trim() !== ''),

      // Deliberately NOT sending updatedData. saveAdminChanges() above has
      // already persisted every edit, and this second partial update fought it:
      // its header fields came from `claimData` - the last LOADED snapshot - so
      // approving overwrote the admin's just-saved business purpose and dates,
      // and its dailyDetails were applied positionally, dropping any line the
      // admin had added. Approving now has exactly the same effect as Save
      // Changes followed by approve, which is what the expense form already did.

      // This is false because we're processing (approve/reject/return), not just editing
      isEditOnly: false,
    };

    // Save any edited field values before submitting the action
    await saveAdminChanges();

    await PerDiemService.submitAdminAction(claimId, actionData);

    setShowActionModal(false);
    setSelectedAction('');
    setAdminDecisionComment('');

    const actionText = selectedAction === 'approve' ? 'approved' : 
                      selectedAction === 'reject' ? 'rejected' : 'sent back for revision';
    alert(`Claim has been ${actionText} successfully!`);

    window.location.href = '/admin/dashboard';

  } catch (error) {
    console.error('Failed to process admin action:', error);
    // Surface the server's reason - it names what is actually missing (an
    // unassigned PTAE, a claim locked into a report under review). The old
    // blanket "please try again" hid that, leaving the admin to retry an
    // action that could never succeed.
    const serverMessage = error.response?.data?.message || error.message;
    setError(
      serverMessage
        ? `Could not ${selectedAction} this claim: ${serverMessage}`
        : `Failed to ${selectedAction} claim. Please try again.`
    );
  } finally {
    setIsLoading(false);
  }
}, [selectedAction, adminDecisionComment, lineItems, supportingDocuments, setError, setIsLoading, saveAdminChanges, alert]);


const withdrawApproval = useCallback(async () => {
  if (!claimId) return;
  if (!await confirm(
    'Withdraw the approval on this claim?\n\nIt will return to Under Review, ' +
    'leave the auditor queue, and become editable again.'
  )) return;
  setIsLoading(true);
  setError(null);
  try {
    await PerDiemService.withdrawApproval(claimId);
    await loadClaimData(claimId);
  } catch (err) {
    const serverMessage = err.response?.data?.message || err.message;
    setError(`Could not withdraw this approval: ${serverMessage}`);
  } finally {
    setIsLoading(false);
  }
}, [claimId, loadClaimData, confirm]);

const handleAdminAction = useCallback(() => {
  setShowActionModal(true);
}, []);







  return {
    // Form state
    formData: {
      lastName, setLastName,
      firstName, setFirstName,
      position, setPosition,
      businessPurpose, setBusinessPurpose,
      receivedAdvance, setReceivedAdvance,
      startDate, setStartDate,
      endDate, setEndDate,
    },
    
    // Line items and city search
    lineItems, setLineItems,
    citySearch, setCitySearch,
    cityResults,
    activeSearchId, setActiveSearchId,
    isSearching,
    setDebouncedSearch,
    quickFillCity,setQuickFillCity,quickFillCountry,setQuickFillCountry,
    
    // Loading and error states
    isLoading,
    error,
    totalAmount,

    setError,
    
    // Functions
    searchCities,
    addLineItem,
    updateLineItem,
    handleCitySelect,
    apply75Percent,
    removeLodging,
    deleteLineItem,
    restoreLineItem,
    appliedRates,
    removedLodgings,
    handleKeyDown,
    searchRef,
    saveForm,
    handleNoLodgingAllToggle,
    handleMealExclusionToggle,
    toggleMealRates,

    isEditMode,
    isViewMode,
    isEditing,
    isReadOnly,
    enableEditing,

    deleteLineItems,
    restoreLineItems,
    deletePerDiemClaim, resetDeleteState,
    savedDraftId,
    toggleAdminComment, saveAdminComment,

    showActionModal,setShowActionModal,
    selectedAction, setSelectedAction,
    adminDecisionComment, setAdminDecisionComment,

    handleAdminAction,
    withdrawApproval, handleModalSubmit,
    saveAdminChanges,
    isSaving, saveSuccess,
    loadClaimData,

    claimStatus,
    claimData,
    adminComments,
    processedBy,
    processedAt,
    canEditClaim,

    // Supporting documents
    supportingDocuments,
    uploadSupportingDocuments, deleteSupportingDocument,
    isUploadingDocuments,

    isNewClaim: isCreateRoute && !claimId, // True only for create routes without claimId
    onBehalfOf,
    isCreateRoute,

    showCustomCityInput,
    setShowCustomCityInput,
    customCityName,
    setCustomCityName,
    customCityCountry,
    setCustomCityCountry,
    handleCustomCitySubmit,
    handleCustomCityCancel,
    setCityResults,
    
  };
};

